import {
  InPlaceHeaderEditor,
  InPlaceMultilineEditor,
  Tooltip,
} from "@etframework/ui.components";
import { toast } from "react-toastify";
import { ClientsDropdownInPlaceEditor } from "./ClientsDropdownInPlaceEditor";
import { useTranslation } from "react-i18next";
import { useUpdateTask } from "../../data/mutations/taskMutations";
import { useQueryClient } from "@tanstack/react-query";
import useEvent from "../../hooks/useEvent";
import { TagsMultiSelectInPlaceEditor } from "./TagsMultiSelectInPlaceEditor";
import { useRef } from "react";
import { CopyLinkComponent } from "./CopyLinkComponent";
import { useDueDatesAuth } from "../../hooks/useDueDatesAuth";

export const TaskConsistentDataContainer = ({ ids, task, canModifyTasks }) => {
  const { t } = useTranslation(["Overlay", "Shared"], {
    useSuspense: false,
  });

  const { UserId } = useDueDatesAuth();
  const { mutate: updateTask } = useUpdateTask();
  const queryClient = useQueryClient();

  const tagsInPlaceEditorRef = useRef(task?.Tags);

  //Hooks
  const { Events, toggleEvent } = useEvent();

  //Compare if selected tag ids are the same as the
  //received server ids.
  const verifyIfSameIds = (object, array) => {
    const objectIds = new Set(object);
    // Convert array to a Set for easy comparison
    const arrayIds = new Set(array);
    // Check if both sets are equal
    return (
      objectIds.size === arrayIds.size &&
      [...objectIds]?.every((id) => arrayIds.has(id))
    );
  };

  const onTaskModified = (e) => {
    const idRegex =
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
    let modifiedTask = {
      ...task,
      TemplateId: task?.Template?.Id,
      CurrentUserId: UserId,
      Collaborators: null,
      Tags: null,
      AssigneeId: task?.Assignee?.Id,
    };

    if (e?.id) {
      // this triggers on `actionSuccess` event of InPlaceEditors
      let value = e.value;
      switch (e.id) {
        case ids.name:
          value = value.trim?.().trimStart?.();
          if (value == task?.Name) return;
          modifiedTask = {
            ...modifiedTask,
            Name: value,
          };
          break;
        case ids.client:
          if (value == task?.Client?.Id) return;
          modifiedTask = {
            ...modifiedTask,
            ClientId: value,
          };
          break;
        case ids.description:
          if (value == task?.Description) return;
          modifiedTask = {
            ...modifiedTask,
            Description: value,
          };
          break;
        case ids.tags:
          if (
            verifyIfSameIds(
              task?.Tags?.map((obj) => obj.Id),
              tagsInPlaceEditorRef.current?.getValue()
            )
          )
            return;
          modifiedTask = {
            ...modifiedTask,
            Tags: tagsInPlaceEditorRef.current
              .getValue()
              .map((value) =>
                idRegex.test(value.Title)
                  ? { Id: value.Title }
                  : { Title: value.Title }
              ),
          };
          break;
        default:
          break;
      }
    }

    updateTask(
      { task: modifiedTask },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(["get-task", task?.Id]);
          toggleEvent(Events.RefreshTaskGrid);
          toast.success(t("Success"), { position: "bottom-left" });
        },
        onError: () => {
          toast.error(t("ErrorMsg"), { position: "bottom-left" });
        },
      }
    );
  };

  const onInPlaceEditorValidation = (e) => {
    if (e.data.value == "Empty") {
      e.errorMessage = t("Shared:Required");
      return;
    }
  };

  return (
    <div className="mb-4">
      <div className="d-flex justify-content-between flex-row align-items-center">
        <CopyLinkComponent ids={ids} task={task} />
      </div>

      <div>
        <InPlaceHeaderEditor
          id={ids.name}
          value={task?.Name}
          name="Name"
          htmlAttributes={{
            maxlength: "100",
          }}
          validationRules={{
            Name: { required: true },
          }}
          validating={onInPlaceEditorValidation}
          actionSuccess={(e) => {
            e.id = ids.name;
            onTaskModified(e);
          }}
          disabled={!canModifyTasks}
        />
      </div>

      <div className="d-flex mx-2 align-items-center gap-2">
        <div className="col-auto">{t("For")}</div>
        <div className="col task-client">
          <ClientsDropdownInPlaceEditor
            id={ids.client}
            value={task?.Client?.Id}
            actionSuccess={onTaskModified}
            disabled={!canModifyTasks}
          />
        </div>
      </div>

      <div className="d-flex mx-2 mt-3 gap-2">
        <div className="col-auto mt-2">
          <Tooltip
            content={t("Description")}
            showTipPointer={false}
            position="Top"
            target="#task-overlay-description-icon"
            dark
            closeDelay={100_000}
          >
            <i
              id="task-overlay-description-icon"
              className="fa-regular fa-align-left task-overlay-input-icon"
            ></i>
          </Tooltip>
        </div>
        <div className="col">
          <InPlaceMultilineEditor
            id={ids.description}
            className={"task-description"}
            value={task?.Description}
            change={onTaskModified}
            emptyText={t("AddDescription")}
            disabled={!canModifyTasks}
            htmlAttributes={{
              maxlength: "2000",
            }}
          />
        </div>
      </div>

      <div className="d-flex align-items-center gap-2 mx-2 mt-3">
        <div>
          <Tooltip
            content={t("Tags")}
            showTipPointer={false}
            position="Top"
            target="#task-overlay-tags-icon"
            dark
            closeDelay={100_000}
          >
            <i
              id={"task-overlay-tags-icon"}
              className="fa-regular fa-tags task-overlay-input-icon"
            ></i>
          </Tooltip>
        </div>
        <div className="w-100">
          <TagsMultiSelectInPlaceEditor
            id={ids.tags}
            ref={tagsInPlaceEditorRef}
            value={task?.Tags}
            actionSuccess={onTaskModified}
            disabled={!canModifyTasks}
          />
        </div>
      </div>
    </div>
  );
};
